import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Switch,
  Route,
  useHistory,
  useLocation,
  Redirect,
} from 'react-router-dom';
import debounce from 'lodash.debounce';

import { reportError } from 'venuescanner-js/react/services/errorService';
import inboxService from 'venuescanner-js/react/components/Inbox/services/inboxService';
import observableToUseEffect from 'venuescanner-js/react/components/Inbox/utils/observableToUseEffect';
import setCustomViewportUnits from 'venuescanner-js/react/components/Inbox/helpers/setCustomViewportUnits';
import { ViewContext } from 'venuescanner-js/react/components/Inbox/context/ViewContext';
import { EnquiryContext } from 'venuescanner-js/react/components/Inbox/context/EnquiryContext';
import { inboxMobileView, status } from 'venuescanner-js/constants/thread';
import store from 'venuescanner-js/services/inbox/models/store';

import Chat from 'venuescanner-js/react/components/Inbox/components/Chat';
import NavigationBar from 'venuescanner-js/react/components/Inbox/components/NavigationBar';
import Enquiry from 'venuescanner-js/react/components/Inbox/components/Enquiry';
import Leads from 'venuescanner-js/react/components/Inbox/components/Leads';
import Matches from 'venuescanner-js/react/components/Inbox/components/Matches';
import ErrorFallback from 'venuescanner-js/react/components/Inbox/components/Objects/ErrorFallback';
import EnquiryMenu from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryMenu';
import { useMachine } from '@xstate/react';
import { enquiryMachine } from './enquiryMachine/enquiryMachine';
import countries from 'venuescanner-js/constants/countries.json';
import { PaymentSuccess } from './PaymentSuccess';
import { Summary } from './components/Summary/Summary';
import YourRecentEnquiriesModal from './components/YourRecentEnquiries';
import { isHost } from 'venuescanner-js/constants/user_type';

const Inbox = () => {
  const {
    activeMobileView,
    isMobileDevice,
    setIsMobileDevice,
    setIsMonitor,
    activeThread,
    threadsFilter,
    setActiveCurrency,
    setActiveMobileView,
  } = useContext(ViewContext);
  const { threads, organiserLeads, hostLeads } = useContext(EnquiryContext);
  const [initThread, setInitThread] = useState();
  const history = useHistory();
  const location = useLocation();

  // set custom viewport units on first load
  useEffect(() => {
    setCustomViewportUnits();
    window.addEventListener('resize', () => setCustomViewportUnits());
  }, []);

  // setIsMobileDevice on window resize
  const handleResize = () => {
    setIsMobileDevice(window.innerWidth < 1200);
    setIsMonitor(window.innerWidth >= 1400);
  };
  window.addEventListener(
    'resize',
    debounce(handleResize, 500, { leading: true, trailing: true }),
  );

  // set first thread when the thread list updates if NOT mobile AND no active thread is present
  useEffect(() => {
    if (!isMobileDevice && !activeThread) {
      const firstThread = { ...threads[0] };
      return observableToUseEffect(
        { firstThread },
        async ({ firstThread }) => setInitThread(firstThread),
        [firstThread],
      );
    }
  }, [threads]);

  // load first thread if NOT mobile AND no active thread is present AND first thread is present
  useEffect(() => {
    if (!isMobileDevice && !activeThread && initThread?._id) {
      const checkIfisInboxOnly = new RegExp(/^\/inbox\/?$/);

      inboxService
        .loadThread(initThread._id)
        .then(_res => {
          if (checkIfisInboxOnly.test(location.pathname)) {
            history.push(`/inbox/${initThread._id}`);
          }
        })
        .catch(_err => {});
    }
  }, [initThread, threadsFilter]);

  return (
    <div className='inbox'>
      <div className='inbox__wrapper'>
        <Switch>
          <Route exact path={['/inbox/:thread_id/success']}>
            {activeThread && (
              <ThreadSubscriber threadObservable={activeThread}>
                {({ venue }) => <PaymentSuccess venue={venue} />}
              </ThreadSubscriber>
            )}
          </Route>
          <Route exact path={'/inbox/:thread_id/summary'}>
            {activeThread && (
              <ThreadSubscriber threadObservable={activeThread}>
                {({ thread, space, venue }) =>
                  thread.status === status.ACCEPTED ? (
                    <Summary
                      activeMobileView={activeMobileView}
                      isMobileDevice={isMobileDevice}
                      setActiveMobileView={setActiveMobileView}
                      thread={thread}
                      space={space}
                      venue={venue}
                    />
                  ) : (
                    <Redirect to='/inbox' />
                  )
                }
              </ThreadSubscriber>
            )}
          </Route>
          <Route exact path={['/inbox/matches']}>
            <NavigationBar />
            {organiserLeads ? (
              <Matches />
            ) : (
              <div className='inbox__placeholder' />
            )}
          </Route>
          <Route exact path={['/inbox/matches/:match_id']}>
            <NavigationBar />
            {organiserLeads ? (
              <Matches skipOnBoarding={true} />
            ) : (
              <div className='inbox__placeholder' />
            )}
          </Route>
          <Route exact path={['/inbox/leads', '/inbox/leads/:lead_id']}>
            <NavigationBar />
            {hostLeads ? <Leads /> : <div className='inbox__placeholder' />}
          </Route>
          {isMobileDevice && (
            <Route exact path='/inbox'>
              <NavigationBar />
            </Route>
          )}
          <Route path={['/inbox', '/inbox/:thread_id']}>
            {!isMobileDevice && <NavigationBar />}
            {!activeThread && !isMobileDevice && (
              <div className='inbox__placeholder' />
            )}
            {activeThread && (
              <>
                <ThreadSubscriber threadObservable={activeThread}>
                  {({ thread, space, venue, organiser, host, concierge }) => (
                    <Thread
                      activeMobileView={activeMobileView}
                      isMobileDevice={isMobileDevice}
                      setActiveCurrency={setActiveCurrency}
                      setActiveMobileView={setActiveMobileView}
                      thread={thread}
                      venue={venue}
                      space={space}
                      organiser={organiser}
                      host={host}
                      concierge={concierge}
                    />
                  )}
                </ThreadSubscriber>
              </>
            )}
          </Route>
        </Switch>
      </div>
    </div>
  );
};

function ThreadSubscriber({ threadObservable, children }) {
  const [thread, setThread] = useState(null);
  const [space, setSpace] = useState(null);
  const [venue, setVenue] = useState(null);
  const [concierge, setConcierge] = useState(null);
  const [organiser, setOrganiser] = useState(null);
  const [host, setHost] = useState(null);

  useEffect(() => {
    return observableToUseEffect(
      { activeThread: threadObservable },
      ({ activeThread }) => {
        if (!activeThread) return;

        const space = store.space[activeThread._space._id];
        const venue = store.venue[activeThread._venue._id];
        const organiser = store.organiser[activeThread._organiser?._id];
        const host = store.host[activeThread._host?._id];
        const concierge = activeThread.concierge
          ? activeThread._concierge
          : null;

        setThread(activeThread);
        // We should save the data, not the observable.
        // But we need the getPhoto function, which is not in the space data
        setSpace(space);
        setOrganiser(organiser ? organiser.data() : null);
        setVenue(venue.data());
        setHost(host ? host.data() : null);
        setConcierge(concierge);
      },
    );
  }, [threadObservable]);

  if (!thread) return null;

  return children({ thread, space, venue, organiser, host, concierge });
}

ThreadSubscriber.propTypes = {
  threadObservable: PropTypes.object.isRequired,
  children: PropTypes.func.isRequired,
};

function Thread({
  thread,
  space,
  venue,
  organiser,
  host,
  concierge,
  isMobileDevice,
  setActiveCurrency,
  activeMobileView,
  setActiveMobileView,
}) {
  const [showYourRecentEnquiriesModal, setShowYourRecentEnquiriesModal] =
    useState(false);
  const searchParams = useLocation().search;
  const { activeCurrency } = useContext(ViewContext);
  const SUCCESS_DELAY = 4000;
  const history = useHistory();

  function setActiveViewToChat() {
    history.push(history.location.pathname);

    setActiveMobileView(inboxMobileView.CHAT);
  }

  const [state, send] = useMachine(enquiryMachine, {
    context: {
      thread,
      space,
      venue,
      organiser,
      host,
      concierge,
      setActiveViewToChat,
      isMobileDevice,
      searchParams,
      successDelay: SUCCESS_DELAY,
    },
    devTools: true,
  });

  useEffect(() => {
    send({
      type: 'THREAD_CHANGED',
      thread,
      space,
      venue,
      organiser,
      concierge,
      host,
    });
  }, [concierge, organiser, send, space, thread, venue, host]);

  useEffect(() => {
    const timeout = setTimeout(
      () => setShowYourRecentEnquiriesModal(true),
      200,
    );

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (isMobileDevice)
      setActiveCurrency(countries[venue.country_code || 'GB']);
  }, [isMobileDevice, setActiveCurrency, thread, venue.country_code]);

  const canShowYourRecentEnquiriesModal =
    showYourRecentEnquiriesModal && isHost(thread);

  if (isMobileDevice) {
    return (
      <>
        {activeMobileView === inboxMobileView.CHAT && (
          <Chat thread={thread} venue={venue} />
        )}
        {activeMobileView === inboxMobileView.ENQUIRY && activeCurrency && (
          <Enquiry state={state} send={send} status={thread?.status} />
        )}
        {activeMobileView === inboxMobileView.CHAT && (
          <EnquiryMenu thread={thread} state={state} send={send} />
        )}
        {canShowYourRecentEnquiriesModal && <YourRecentEnquiriesModal />}
      </>
    );
  }

  return (
    <>
      <>
        <Chat thread={thread} venue={venue} />
        {activeCurrency && <Enquiry status={thread?.status} state={state} send={send} />}
        {showYourRecentEnquiriesModal && <YourRecentEnquiriesModal />}
      </>
    </>
  );
}

Thread.propTypes = {
  activeMobileView: PropTypes.string.isRequired,
  concierge: PropTypes.bool,
  host: PropTypes.object.isRequired,
  isMobileDevice: PropTypes.bool.isRequired,
  organiser: PropTypes.object.isRequired,
  setActiveCurrency: PropTypes.func.isRequired,
  setActiveMobileView: PropTypes.func.isRequired,
  space: PropTypes.object.isRequired,
  thread: PropTypes.object.isRequired,
  venue: PropTypes.shape({
    country_code: PropTypes.string,
  }).isRequired,
};

const InboxWrapped = () => {
  return (
    <div style={{ background: '#ececec' }}>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={() => window.location.reload()}
        onError={error => reportError('Inbox', error)}>
        <Inbox />
      </ErrorBoundary>
    </div>
  );
};

export default InboxWrapped;
